import { useCallback, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

/**
 * User search for the admin `UserPicker` (SSA-955) — the first user-search
 * component on the admin site.
 *
 * There is no name-search endpoint on the admin API today, so the picker routes
 * a single input by what was typed:
 *
 *   user id (UUID)  GET /admin/gamification/users/{userId} — resolves and, just
 *                   as importantly, CONFIRMS the account before a payout is
 *                   bound to it (account status, wallet, fraud flag).
 *   4 digits        GET /admin/gamification/users?phoneLast4=NNNN — the masked
 *                   candidate list; the full number is never returned.
 *   email           matched against `directory`; if it isn't there the address
 *                   is carried as an unresolved pick and the referral endpoints
 *                   resolve it server-side via `owner_email` (they reject an
 *                   address that matches zero or multiple live accounts).
 *   anything else   name substring over `directory`.
 *
 * `directory` is supplied by the caller — a list of users it already knows about
 * (e.g. the promoters bound to existing referral codes, which is where display
 * names and emails actually come from today). Keeping it a prop means the picker
 * itself stays feature-agnostic.
 */

export type UserSearchMode = 'user_id' | 'phone_last4' | 'email' | 'name';

export interface FoundUser {
  /** null when only an email was supplied — resolved server-side on save. */
  user_id: string | null;
  email: string | null;
  display_name: string | null;
  phone_masked?: string | null;
  account_status?: string | null;
  is_flagged?: boolean;
  wallet_balance?: number | null;
  /** Where this candidate came from, for the "how was this matched" line. */
  source: 'lookup' | 'directory' | 'unresolved_email';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LAST4_RE = /^\d{4}$/;

export function detectSearchMode(term: string): UserSearchMode {
  const t = term.trim();
  if (UUID_RE.test(t)) return 'user_id';
  if (LAST4_RE.test(t)) return 'phone_last4';
  if (t.includes('@')) return 'email';
  return 'name';
}

/** Shape returned by the gamification user endpoints. */
interface UserRecord {
  user_id: string;
  display_name?: string | null;
  email?: string | null;
  phone_masked: string | null;
  account_status: string | null;
  wallet_balance: number;
  total_earned: number;
  is_flagged: boolean;
  flagged_reason: string | null;
}

const fromRecord = (u: UserRecord): FoundUser => ({
  user_id: u.user_id,
  // The lookup endpoint does not select users.display_name / email today; the
  // caller's directory fills those in when it knows the account.
  display_name: u.display_name ?? null,
  email: u.email ?? null,
  phone_masked: u.phone_masked,
  account_status: u.account_status,
  is_flagged: u.is_flagged,
  wallet_balance: u.wallet_balance,
  source: 'lookup',
});

const matchesTerm = (u: FoundUser, term: string): boolean => {
  const t = term.toLowerCase();
  return (
    (u.display_name ?? '').toLowerCase().includes(t) ||
    (u.email ?? '').toLowerCase().includes(t)
  );
};

export function useUserSearch(env: Env, directory: FoundUser[] = []) {
  const { request } = useAdminApi();
  const [results, setResults] = useState<FoundUser[]>([]);
  const [mode, setMode] = useState<UserSearchMode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setResults([]);
    setMode(null);
    setError(null);
  }, []);

  const search = useCallback(
    async (rawTerm: string): Promise<FoundUser[]> => {
      const term = rawTerm.trim();
      const searchMode = detectSearchMode(term);
      setMode(searchMode);
      setError(null);

      // Directory hits are free and always worth showing — a UUID or last-4
      // match that is also a known promoter gets its display name from here.
      const known = directory.filter((u) => matchesTerm(u, term));

      if (searchMode === 'name') {
        setResults(known);
        return known;
      }

      if (searchMode === 'email') {
        const exact = directory.filter((u) => (u.email ?? '').toLowerCase() === term.toLowerCase());
        const out: FoundUser[] = exact.length
          ? exact
          : [{ user_id: null, email: term, display_name: null, source: 'unresolved_email' }];
        setResults(out);
        return out;
      }

      setLoading(true);
      try {
        let found: FoundUser[];
        if (searchMode === 'user_id') {
          const record = await request<UserRecord>({
            env,
            path: `/admin/gamification/users/${term}`,
          });
          found = [fromRecord(record)];
        } else {
          const data = await request<{ items: UserRecord[] }>({
            env,
            path: '/admin/gamification/users',
            query: { phoneLast4: term },
          });
          found = (data.items ?? []).map(fromRecord);
        }

        // Enrich with any name/email the directory knows for the same account.
        const byId = new Map(directory.filter((u) => u.user_id).map((u) => [u.user_id!, u]));
        const enriched = found.map((u) => {
          const hit = u.user_id ? byId.get(u.user_id) : undefined;
          return hit ? { ...u, display_name: u.display_name ?? hit.display_name, email: u.email ?? hit.email } : u;
        });
        setResults(enriched);
        return enriched;
      } catch (e) {
        const notFound = e instanceof ApiError && e.status === 404;
        setError(notFound ? 'No account with that id.' : e instanceof ApiError ? e.message : 'Lookup failed');
        setResults([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [request, env, directory],
  );

  return { results, mode, loading, error, search, reset };
}
